// src/components/property/SceneVideoLightbox.tsx
/**
 * SceneVideoLightbox Component
 *
 * Full-screen lightbox for viewing scene videos with navigation.
 * Supports keyboard navigation (arrows, escape) and scene switching.
 */

import { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  ISceneVideoLightboxProps,
  IPropertySceneExtended,
} from '@/@types/propertySceneEditor';
import type { Id } from '../../../convex/_generated/dataModel';

/**
 * Props for LightboxVideo helper component
 */
interface ILightboxVideoProps {
  storageId: Id<'_storage'>;
  aspectRatio: '16:9' | '9:16';
}

/**
 * Helper component to load and display a single video
 * Memoized to prevent unnecessary re-renders during playback
 */
const LightboxVideo = memo(function LightboxVideo({ storageId, aspectRatio }: ILightboxVideoProps) {
  const videoUrl = useQuery(api.storage.getUrl, { storageId });
  const videoRef = useRef<HTMLVideoElement>(null);

  // Auto-play when loaded
  useEffect(() => {
    if (videoUrl && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Ignore autoplay errors (browser policy)
      });
    }
  }, [videoUrl]);

  if (!videoUrl) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-8 w-8 text-white animate-spin" />
      </div>
    );
  }

  const videoClasses =
    aspectRatio === '9:16' ? 'max-h-[85vh] max-w-[50vw]' : 'max-h-[70vh] max-w-[90vw]';

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      className={cn(videoClasses, 'object-contain rounded-lg')}
      controls
      autoPlay
      playsInline
    />
  );
});

/**
 * Format scene type for display
 */
function formatSceneType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
}

/**
 * Props for inner lightbox content component
 */
interface ILightboxContentProps {
  onClose: () => void;
  scenesWithVideos: IPropertySceneExtended[];
  initialIndex: number;
  aspectRatio: '16:9' | '9:16';
}

/**
 * Inner content component for the lightbox
 * This is rendered with a key to reset state when modal opens
 */
function LightboxContent({
  onClose,
  scenesWithVideos,
  initialIndex,
  aspectRatio,
}: ILightboxContentProps) {
  const [currentIndex, setCurrentIndex] = useState(() => initialIndex);

  const hasMultipleVideos = scenesWithVideos.length > 1;

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : scenesWithVideos.length - 1));
  }, [scenesWithVideos.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < scenesWithVideos.length - 1 ? prev + 1 : 0));
  }, [scenesWithVideos.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, onClose]);

  const currentScene = scenesWithVideos[currentIndex];

  if (!currentScene || !currentScene.generatedVideoId) {
    return null;
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <h2 className="text-lg font-medium">
          Scene {currentScene.sceneNumber}: {formatSceneType(currentScene.sceneType)}
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Video container */}
      <div className="flex-1 flex items-center justify-center px-16 relative">
        {/* Navigation - Previous */}
        {hasMultipleVideos && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            aria-label="Previous scene"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
        )}

        <LightboxVideo storageId={currentScene.generatedVideoId} aspectRatio={aspectRatio} />

        {/* Navigation - Next */}
        {hasMultipleVideos && (
          <button
            onClick={goToNext}
            className="absolute right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            aria-label="Next scene"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        )}
      </div>

      {/* Footer with navigation buttons */}
      <div className="flex items-center justify-between p-4">
        <Button
          variant="outline"
          onClick={goToPrevious}
          disabled={!hasMultipleVideos}
          className="text-white border-white/30 hover:bg-white/10"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous Scene
        </Button>

        {/* Scene counter */}
        {hasMultipleVideos && (
          <div className="px-4 py-2 rounded-full bg-black/50 text-white text-sm">
            {currentIndex + 1} of {scenesWithVideos.length}
          </div>
        )}

        <Button
          variant="outline"
          onClick={goToNext}
          disabled={!hasMultipleVideos}
          className="text-white border-white/30 hover:bg-white/10"
        >
          Next Scene
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

/**
 * SceneVideoLightbox - Full-screen video viewer with scene navigation
 *
 * Uses a key-based approach to remount content when modal opens,
 * ensuring the initial scene index is respected each time.
 */
function SceneVideoLightbox({
  isOpen,
  onClose,
  scenes,
  initialSceneIndex,
  aspectRatio,
}: ISceneVideoLightboxProps) {
  // Filter to only scenes with completed videos
  const scenesWithVideos = useMemo(
    () =>
      scenes.filter(scene => scene.imageToVideoStatus === 'completed' && scene.generatedVideoId),
    [scenes]
  );

  // Calculate initial index from scenes and initialSceneIndex
  const computedInitialIndex = useMemo(() => {
    const targetScene = scenes[initialSceneIndex];
    const newIndex = scenesWithVideos.findIndex(s => s._id === targetScene?._id);
    return newIndex >= 0 ? newIndex : 0;
  }, [scenes, scenesWithVideos, initialSceneIndex]);

  // Don't render if no videos available
  if (scenesWithVideos.length === 0) {
    return null;
  }

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 outline-none"
          onPointerDownOutside={e => e.preventDefault()}
        >
          {isOpen && (
            <LightboxContent
              key={`lightbox-${initialSceneIndex}-${computedInitialIndex}`}
              onClose={onClose}
              scenesWithVideos={scenesWithVideos}
              initialIndex={computedInitialIndex}
              aspectRatio={aspectRatio}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export { SceneVideoLightbox };
